/**
 * Shipping Utility - Domestic & International Shipping Logic
 * Handles shipping options, costs, and delivery times for both domestic and international orders
 */

// Domestic shipping options (India)
const DOMESTIC_SHIPPING_OPTIONS = [
  {
    id: 'standard',
    name: 'Standard Delivery',
    days: 7,
    charge: (packageTotal) => packageTotal >= 500 ? 0 : 58,
    description: 'Regular shipping within India',
    available: true
  },
  {
    id: 'express',
    name: 'Express Delivery',
    days: 3,
    charge: (packageTotal) => packageTotal >= 500 ? 99 : 149,
    description: 'Faster delivery within India',
    available: true
  },
  {
    id: 'same_day',
    name: 'Same Day Delivery',
    days: 1,
    charge: (packageTotal) => packageTotal >= 500 ? 199 : 249,
    description: 'Same day delivery (select cities only)',
    available: true
  }
];

// International shipping options
const INTERNATIONAL_SHIPPING_OPTIONS = [
  {
    id: 'standard_international',
    name: 'Standard International',
    days: 15,
    charge: (packageTotal, country) => {
      // Base charge varies by region
      const baseCharge = getInternationalBaseCharge(country);
      return baseCharge + (packageTotal * 0.15); // 15% of order value
    },
    description: 'Standard international shipping',
    available: true
  },
  {
    id: 'express_international',
    name: 'Express International',
    days: 7,
    charge: (packageTotal, country) => {
      const baseCharge = getInternationalBaseCharge(country);
      return baseCharge + (packageTotal * 0.25); // 25% of order value
    },
    description: 'Express international shipping',
    available: true
  },
  {
    id: 'premium_international',
    name: 'Premium International',
    days: 3,
    charge: (packageTotal, country) => {
      const baseCharge = getInternationalBaseCharge(country);
      return baseCharge + (packageTotal * 0.35); // 35% of order value
    },
    description: 'Premium fast international shipping',
    available: true
  }
];

// International shipping base charges by region
const INTERNATIONAL_BASE_CHARGES = {
  // South Asia
  'IN': 0, // Domestic
  'BD': 800, // Bangladesh
  'PK': 1200, // Pakistan
  'NP': 600, // Nepal
  'LK': 1000, // Sri Lanka
  'BT': 700, // Bhutan
  'MV': 1500, // Maldives
  
  // Middle East
  'AE': 2000, // UAE
  'SA': 2500, // Saudi Arabia
  'KW': 2200, // Kuwait
  'QA': 2300, // Qatar
  'OM': 2100, // Oman
  'BH': 2000, // Bahrain
  
  // Asia Pacific
  'SG': 1800, // Singapore
  'MY': 1500, // Malaysia
  'TH': 1400, // Thailand
  'ID': 1600, // Indonesia
  'PH': 1700, // Philippines
  'VN': 1500, // Vietnam
  
  // Europe
  'GB': 3500, // UK
  'DE': 3200, // Germany
  'FR': 3200, // France
  'IT': 3200, // Italy
  'ES': 3200, // Spain
  'NL': 3100, // Netherlands
  
  // Americas
  'US': 4500, // USA
  'CA': 4200, // Canada
  'MX': 3000, // Mexico
  'BR': 3500, // Brazil
  
  // Africa
  'ZA': 4000, // South Africa
  'KE': 3500, // Kenya
  'NG': 3800, // Nigeria
  
  // Default for other countries
  'DEFAULT': 3000
};

/**
 * Get international base charge for a country
 * @param {string} countryCode - ISO country code
 * @returns {number} Base shipping charge
 */
export const getInternationalBaseCharge = (countryCode) => {
  if (!countryCode) return INTERNATIONAL_BASE_CHARGES.DEFAULT;
  const code = countryCode.toUpperCase();
  return INTERNATIONAL_BASE_CHARGES[code] || INTERNATIONAL_BASE_CHARGES.DEFAULT;
};

/**
 * Check if address is domestic (India) or international
 * @param {Object} address - Shipping address object
 * @returns {boolean} True if domestic (India), false if international
 */
export const isDomesticShipping = (address) => {
  if (!address) return true; // Default to domestic
  
  // Check country field
  const country = address.country || address.cityState?.split(',').pop()?.trim() || '';
  const countryUpper = country.toUpperCase();
  
  // Check if India
  if (countryUpper === 'INDIA' || countryUpper === 'IN' || countryUpper === '') {
    return true;
  }
  
  return false;
};

/**
 * Get shipping options based on address type (domestic/international)
 * @param {Object} address - Shipping address
 * @param {number} packageTotal - Total value of package
 * @returns {Array} Available shipping options
 */
export const getShippingOptions = (address, packageTotal = 0) => {
  const isDomestic = isDomesticShipping(address);
  
  if (isDomestic) {
    return DOMESTIC_SHIPPING_OPTIONS.map(option => ({
      ...option,
      charge: option.charge(packageTotal),
      estimatedCharge: option.charge(packageTotal)
    }));
  } else {
    const countryCode = extractCountryCode(address);
    return INTERNATIONAL_SHIPPING_OPTIONS.map(option => ({
      ...option,
      charge: option.charge(packageTotal, countryCode),
      estimatedCharge: option.charge(packageTotal, countryCode)
    }));
  }
};

/**
 * Extract country code from address
 * @param {Object} address - Shipping address
 * @returns {string} Country code
 */
export const extractCountryCode = (address) => {
  if (!address) return 'IN';
  
  // Try to get from country field
  if (address.country) {
    // If it's a code (2-3 letters), return it
    if (address.country.length <= 3) {
      return address.country.toUpperCase();
    }
    // If it's a country name, try to map it
    return mapCountryNameToCode(address.country);
  }
  
  // Try to extract from cityState field
  if (address.cityState) {
    const parts = address.cityState.split(',');
    const countryPart = parts[parts.length - 1]?.trim();
    if (countryPart) {
      return mapCountryNameToCode(countryPart);
    }
  }
  
  return 'IN'; // Default to India
};

/**
 * Map country name to ISO code
 * @param {string} countryName - Country name
 * @returns {string} ISO country code
 */
export const mapCountryNameToCode = (countryName) => {
  if (!countryName) return 'IN';
  
  const name = countryName.toUpperCase();
  const countryMap = {
    'INDIA': 'IN',
    'BANGLADESH': 'BD',
    'PAKISTAN': 'PK',
    'NEPAL': 'NP',
    'SRI LANKA': 'LK',
    'BHUTAN': 'BT',
    'MALDIVES': 'MV',
    'UNITED ARAB EMIRATES': 'AE',
    'UAE': 'AE',
    'SAUDI ARABIA': 'SA',
    'KUWAIT': 'KW',
    'QATAR': 'QA',
    'OMAN': 'OM',
    'BAHRAIN': 'BH',
    'SINGAPORE': 'SG',
    'MALAYSIA': 'MY',
    'THAILAND': 'TH',
    'INDONESIA': 'ID',
    'PHILIPPINES': 'PH',
    'VIETNAM': 'VN',
    'UNITED KINGDOM': 'GB',
    'UK': 'GB',
    'GERMANY': 'DE',
    'FRANCE': 'FR',
    'ITALY': 'IT',
    'SPAIN': 'ES',
    'NETHERLANDS': 'NL',
    'UNITED STATES': 'US',
    'USA': 'US',
    'CANADA': 'CA',
    'MEXICO': 'MX',
    'BRAZIL': 'BR',
    'SOUTH AFRICA': 'ZA',
    'KENYA': 'KE',
    'NIGERIA': 'NG'
  };
  
  return countryMap[name] || 'IN';
};

/**
 * Calculate delivery date based on shipping method and address
 * @param {string} shippingMethodId - Shipping method ID
 * @param {Object} address - Shipping address
 * @param {number} packageNumber - Package number (for domestic multi-package)
 * @returns {string} Estimated delivery date
 */
export const calculateDeliveryDate = (shippingMethodId, address, packageNumber = 1) => {
  const isDomestic = isDomesticShipping(address);
  const today = new Date();
  
  let daysToAdd = 0;
  
  if (isDomestic) {
    const method = DOMESTIC_SHIPPING_OPTIONS.find(m => m.id === shippingMethodId);
    daysToAdd = method ? method.days : 7;
    // Add extra day for each additional package
    if (packageNumber > 1) {
      daysToAdd += (packageNumber - 1) * 0.5;
    }
  } else {
    const method = INTERNATIONAL_SHIPPING_OPTIONS.find(m => m.id === shippingMethodId);
    daysToAdd = method ? method.days : 15;
  }
  
  const deliveryDate = new Date(today);
  deliveryDate.setDate(deliveryDate.getDate() + Math.ceil(daysToAdd));
  
  return deliveryDate.toLocaleDateString('en-IN', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get shipping charge for a specific method and package
 * @param {string} shippingMethodId - Shipping method ID
 * @param {number} packageTotal - Package total value
 * @param {Object} address - Shipping address
 * @returns {number} Shipping charge
 */
export const getShippingCharge = (shippingMethodId, packageTotal, address) => {
  const isDomestic = isDomesticShipping(address);
  
  if (isDomestic) {
    const method = DOMESTIC_SHIPPING_OPTIONS.find(m => m.id === shippingMethodId);
    return method ? method.charge(packageTotal) : 0;
  } else {
    const method = INTERNATIONAL_SHIPPING_OPTIONS.find(m => m.id === shippingMethodId);
    const countryCode = extractCountryCode(address);
    return method ? method.charge(packageTotal, countryCode) : 0;
  }
};

/**
 * Check if shipping method is available for address
 * @param {string} shippingMethodId - Shipping method ID
 * @param {Object} address - Shipping address
 * @returns {boolean} True if available
 */
export const isShippingMethodAvailable = (shippingMethodId, address) => {
  const isDomestic = isDomesticShipping(address);
  
  if (isDomestic) {
    return DOMESTIC_SHIPPING_OPTIONS.some(m => m.id === shippingMethodId);
  } else {
    return INTERNATIONAL_SHIPPING_OPTIONS.some(m => m.id === shippingMethodId);
  }
};

/**
 * Get default shipping method for address
 * @param {Object} address - Shipping address
 * @returns {string} Default shipping method ID
 */
export const getDefaultShippingMethod = (address) => {
  const isDomestic = isDomesticShipping(address);
  return isDomestic ? 'standard' : 'standard_international';
};

/**
 * Validate shipping address for international shipping
 * @param {Object} address - Shipping address
 * @returns {Object} Validation result with isValid and errors
 */
export const validateInternationalAddress = (address) => {
  const errors = [];
  
  if (!address) {
    return { isValid: false, errors: ['Address is required'] };
  }
  
  if (!address.country && !address.cityState) {
    errors.push('Country is required for international shipping');
  }
  
  if (!address.address) {
    errors.push('Street address is required');
  }
  
  if (!address.postalCode) {
    errors.push('Postal code is required');
  }
  
  if (!address.mobile) {
    errors.push('Phone number is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  isDomesticShipping,
  getShippingOptions,
  calculateDeliveryDate,
  getShippingCharge,
  isShippingMethodAvailable,
  getDefaultShippingMethod,
  validateInternationalAddress,
  extractCountryCode,
  getInternationalBaseCharge,
  DOMESTIC_SHIPPING_OPTIONS,
  INTERNATIONAL_SHIPPING_OPTIONS
};

